import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { Anlagenzeile, ApiClient } from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  DataTable, EmptyState, ErrorState, ExportLeiste, Feld, KpiCard, KpiReihe, LoadingState, StatusBadge,
  formatiereZahl, vollstaendigkeit, type Spalte,
} from '../../muster';
import { Schemavorschau } from './AnlagenlisteSchema';
import { Zuweisungsdialog } from './AnlagenlisteZuweisung';
import './lueftung.css';

/**
 * Alle Lüftungsanlagen des Projekts, mit ihren Unterlagen.
 *
 * Die Anlage ist der Anker, nicht das Dokument. Die erste Fassung ging vom JSON
 * aus und schlug passende Anlagen vor - bei 123 Anlagen und einem
 * Vorschlagsfilter sah die Liste leer aus, obwohl alle da waren. Hier steht der
 * Bestand, und die Zuweisung geht von der Zeile aus.
 *
 * TV6d (TX-STANDARD 1.6): nach `seiten/lueftung/` umgezogen (Route bleibt
 * `lueftung/anlagen`). Die Liste als PDF über die `ExportLeiste`, je Zeile
 * Anlagendetails als PDF, die **Vorschau der Schemata ASL/SSL** (neu,
 * `AnlagenlisteSchema.tsx`) und die Zuweisung aus JSON (`AnlagenlisteZuweisung.tsx`).
 * Alle Aufrufe wie vorher (`Lueftungsdaten.schreibwege.test.tsx`).
 */
export function Anlagenliste() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [zeilen, setZeilen] = useState<Anlagenzeile[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [suche, setSuche] = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const [dialog, setDialog] = useState<Anlagenzeile | null>(null);
  const [schema, setSchema] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setZeilen(await api.anlagenliste(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [api, projekt.id]);

  useEffect(() => { void laden(); }, [laden]);

  const gefiltert = useMemo(() => (zeilen ?? []).filter((z) => !nurOffen || z.einheiten === 0), [zeilen, nurOffen]);
  const mitDaten = (zeilen ?? []).filter((z) => z.einheiten > 0).length;

  const spalten: Spalte<Anlagenzeile>[] = useMemo(() => [
    {
      schluessel: 'anlage', titel: 'Anlage', immerSichtbar: true,
      wert: (z) => `${z.anlage} ${z.aksSchluessel ?? ''}`.trim(),
      inhalt: (z) => (
        <>
          <Link to={`/projekte/${id}/lueftung`}>{z.anlage}</Link>
          {z.aksSchluessel && <span className="lue-zweitzeile">{z.aksSchluessel}</span>}
        </>
      ),
    },
    {
      schluessel: 'ebene', titel: 'Ebene', wert: (z) => [z.ebene, z.raumnummer].filter(Boolean).join(' '),
      inhalt: (z) => <><span>{z.ebene ?? ''}</span>{z.raumnummer && <span className="lue-zweitzeile">Raum {z.raumnummer}</span>}</>,
    },
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (z) => z.bauteile },
    {
      // Null Einheiten heißt: kein Datenblatt eingelesen - dann kennt die Rohrnetzberechnung ihre Register nicht.
      schluessel: 'einheiten', titel: 'Funktionseinheiten', art: 'zahl', nachkomma: 0, wert: (z) => (z.einheiten > 0 ? z.einheiten : null),
    },
    {
      schluessel: 'geraet', titel: 'Gerät',
      wert: (z) => [z.geraetebezeichnung, z.hersteller, z.angebotsnummer].filter(Boolean).join(' '),
      inhalt: (z) => (z.geraetebezeichnung ? (
        <>
          <span>{z.geraetebezeichnung}</span>
          <span className="lue-zweitzeile">
            {[z.hersteller, z.angebotsnummer, z.datenversion ? `v${z.datenversion}` : null,
              z.fassungen > 1 ? `${formatiereZahl(z.fassungen, 0)} Fassungen` : null].filter(Boolean).join(' · ')}
          </span>
        </>
      ) : null),
    },
    {
      schluessel: 'unterlagen', titel: 'Unterlagen', sortierbar: false, breite: '200px',
      wert: (z) => [z.hatDatenblatt ? 'Datenblatt' : '', z.hatSkizze ? 'Skizze' : ''].filter(Boolean).join(' '),
      inhalt: (z) => <Unterlagenzelle zeile={z} api={api} neuLaden={() => void laden()} />,
    },
    {
      schluessel: 'aktionen', titel: '', sortierbar: false, breite: '330px',
      inhalt: (z) => (
        <Aktionszelle zeile={z} api={api} projektId={projekt.id} zuweisen={() => setDialog(z)} schemata={() => setSchema(z.anlage)} />
      ),
    },
  ], [api, id, laden, projekt.id]);

  const kopf = (
    <PageHeader
      titel="Lüftungsanlagen"
      beschreibung={'Die Anlage ist der Anker, nicht das Dokument: Hier steht der Bestand, und die Zuweisung der '
        + 'Herstellerauslegung geht von der Zeile aus.'}
      meta={<Link to={`/projekte/${id}/lueftung`}>← Lüftungsbestand</Link>}
      aktionen={(
        // Die ganze Liste als Dokument: Übersicht, Kreislaufverbunde und je Anlage ein Abschnitt - mit Dokumentnummer
        // aus dem Berichtsregister (LUE-ANL).
        <ExportLeiste ziele={[{
          art: 'pdf',
          beschriftung: 'Anlagenliste als PDF',
          hinweis: 'Alle Anlagen mit Anlagendetails, Registern, Kanalnetz und KVS-Wärmerückgewinnung (LUE-ANL)',
          laden: () => api.lueftungAnlagenlistePdf(projekt.id),
        }]}
        />
      )}
    />
  );

  if (fehler !== null && !zeilen) {
    return (
      <>
        {kopf}
        <ErrorState meldung="Die Anlagenliste konnte nicht geladen werden." technisch={fehler} beiErneutVersuchen={() => void laden()} />
      </>
    );
  }

  if (!zeilen) {
    return (
      <>
        {kopf}
        <LoadingState text="Lüftungsanlagen werden geladen …" zeilen={10} spalten={6} />
      </>
    );
  }

  const herstellerdaten = vollstaendigkeit(mitDaten, zeilen.length, 100);

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="Anlagen" wert={zeilen.length} />
        {/* Bewertet: Jede Anlage braucht ihre Herstellerauslegung (Grenze 100 %). Ohne Anlagen nichts zu bewerten. */}
        <KpiCard
          titel="mit Herstellerdaten"
          wert={mitDaten}
          vergleich={herstellerdaten.text}
          zustand={zeilen.length === 0 ? undefined : herstellerdaten.zustand}
          zustandText={zeilen.length === 0 ? undefined
            : mitDaten < zeilen.length ? `${formatiereZahl(zeilen.length - mitDaten, 0)} ohne Auslegung` : 'vollständig'}
        />
        <KpiCard titel="mit Datenblatt" wert={zeilen.filter((z) => z.hatDatenblatt).length} />
        <KpiCard titel="mit Geräteskizze" wert={zeilen.filter((z) => z.hatSkizze).length} />
      </KpiReihe>

      <DataTable
        name="Lüftungsanlagen"
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(z) => String(z.id)}
        suchbegriff={suche}
        beiSuche={setSuche}
        suchePlatzhalter="Anlage, Gerät, Angebot …"
        ausfuhr
        ausfuhrDateiname="anlagenliste"
        zeilenhoehe={56}
        zeilenKlasse={(z) => (z.einheiten > 0 ? 'zeile-zugewiesen' : undefined)}
        werkzeuge={<Feld art="haken" beschriftung="Nur ohne Herstellerdaten" wert={nurOffen} beiAenderung={(w) => setNurOffen(w as boolean)} />}
        leer={(
          <EmptyState
            titel={nurOffen ? 'Jede Anlage hat Herstellerdaten.'
              : suche ? 'Keine Anlage passt zur Suche.' : 'Für dieses Modell ist noch keine Lüftungsanlage aufgenommen.'}
            erklaerung={nurOffen
              ? 'Das ist die gute Nachricht - alle Anlagen tragen eine eingelesene Auslegung.'
              : 'Die Anlagen entstehen aus einem Auftrag mit Lüftungsaufnahme; die Auslegung kommt als JSON des Herstellers dazu.'}
            aktion={(nurOffen || suche)
              ? <button type="button" className="m-knopf" onClick={() => { setNurOffen(false); setSuche(''); }}>Filter aufheben</button>
              : undefined}
          />
        )}
      />

      {dialog && (
        <Zuweisungsdialog api={api} projektId={projekt.id} anlage={dialog} schliessen={() => { setDialog(null); void laden(); }} />
      )}
      {schema && <Schemavorschau api={api} projektId={projekt.id} anlage={schema} schliessen={() => setSchema(null)} />}
    </>
  );
}

// ═══ Die Zellen der Anlagenliste ═══════════════════════════════════════════
//
// `DataTable` rendert Zellen statt Zeilen; der Zustand einer Ablage (läuft,
// Fehler) gehört deshalb in die Zelle, die ihn braucht (seit U6b).

/** Beide Unterlagen als Ablegeziel - optional und jederzeit nachtragbar. */
function Unterlagenzelle({ zeile: z, api, neuLaden }: { zeile: Anlagenzeile; api: ApiClient; neuLaden: () => void }) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const hochladen = async (datei: File, art: 'datenblatt' | 'skizze') => {
    if (!datei.name.toLowerCase().endsWith('.pdf')) {
      setFehler('Nur PDF-Dateien.');
      return;
    }
    setLaeuft(art);
    setFehler(null);
    try {
      await api.anlageUnterlage(z.id, datei, art);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <span className="lue-zelle">
      <span className="lue-marken">
        <Ablage titel="Technisches Datenblatt" kurz="Datenblatt" vorhanden={z.hatDatenblatt} dateiname={z.dateiname}
          laeuft={laeuft === 'datenblatt'} ablegen={(d) => void hochladen(d, 'datenblatt')} />
        <Ablage titel="Geräteskizze" kurz="Skizze" vorhanden={z.hatSkizze} dateiname={z.skizzeDateiname}
          laeuft={laeuft === 'skizze'} ablegen={(d) => void hochladen(d, 'skizze')} />
      </span>
      {fehler && <StatusBadge zustand="warnung" text={fehler} klein />}
    </span>
  );
}

function Ablage({ titel, kurz, vorhanden, dateiname, laeuft, ablegen }: {
  titel: string;
  kurz: string;
  vorhanden: boolean;
  dateiname: string | null;
  laeuft: boolean;
  ablegen: (datei: File) => void;
}) {
  const [ueberZiel, setUeberZiel] = useState(false);
  return (
    <label
      className={`m-knopf lue-dateiknopf ${vorhanden ? 'lue-vorhanden' : ''} ${ueberZiel ? 'bereit' : ''}`}
      title={laeuft ? 'wird abgelegt …'
        : vorhanden ? `${titel}: ${dateiname} - andere Datei hierher ziehen` : `${titel} hierher ziehen oder klicken`}
      onDragOver={(e) => { e.preventDefault(); setUeberZiel(true); }}
      onDragLeave={() => setUeberZiel(false)}
      onDrop={(e) => {
        e.preventDefault();
        setUeberZiel(false);
        const d = e.dataTransfer.files?.[0];
        if (d) ablegen(d);
      }}
    >
      {laeuft ? '…' : vorhanden ? `✓ ${kurz}` : `+ ${kurz}`}
      <input
        type="file"
        accept=".pdf"
        className="lue-verborgen"
        disabled={laeuft}
        onChange={(e) => {
          const d = e.target.files?.[0];
          e.target.value = '';
          if (d) ablegen(d);
        }}
      />
    </label>
  );
}

function Aktionszelle({ zeile: z, api, projektId, zuweisen, schemata }: {
  zeile: Anlagenzeile;
  api: ApiClient;
  projektId: string;
  zuweisen: () => void;
  schemata: () => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  return (
    <span className="lue-zelle">
      <span className="lue-marken">
        {/* Der Abschnitt NUR dieser Anlage - eigenes Dokument mit eigener Nummer, damit es einzeln weitergegeben werden kann. */}
        <button
          type="button"
          className="m-knopf"
          disabled={laeuft}
          title="Anlagendetails dieser Anlage als PDF"
          onClick={async () => {
            setLaeuft(true);
            setFehler(null);
            try {
              await api.lueftungAnlagenlistePdf(projektId, z.anlage);
            } catch (e) {
              setFehler(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
        >
          {laeuft ? 'wird erstellt …' : 'Anlagendetails als PDF'}
        </button>
        <button type="button" className="m-knopf" onClick={schemata} title="Anlagen- und Strangschema ansehen und herunterladen">
          Schemata
        </button>
        <button type="button" className="m-knopf" onClick={zuweisen} title="Herstellerauslegung als JSON einlesen">
          {z.einheiten > 0 ? 'aus JSON aktualisieren' : 'Zuweisung aus JSON'}
        </button>
      </span>
      {fehler && <StatusBadge zustand="warnung" text={fehler} klein />}
    </span>
  );
}
